import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FinanceAgeingRow,
  FinanceBreakdownRow,
  FinanceReport,
  FinanceReportQuery,
  FinanceTrend,
  MoneyByCurrency,
} from '@optiflow/types';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { FinanceRecords, type FinanceRecordsRequest } from './FinanceRecords';
import { MoneyByCurrencyText } from './MoneyByCurrencyText';

const NetSalesTrendChart = lazy(() => import('./NetSalesTrendChart'));
const FinanceMoneyChart = lazy(() => import('./FinanceMoneyChart'));

const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG' } as const;

function Stat({
  label,
  children,
  hint,
  action,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card className="min-w-0 p-4">
      <p className="text-muted text-sm">{label}</p>
      <div className="text-ink mt-2 text-2xl font-semibold">{children}</div>
      {hint ? <div className="text-muted mt-1 text-xs">{hint}</div> : null}
      {action}
    </Card>
  );
}

function Section({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-ink font-semibold">
          {title}
        </h2>
        {note ? <p className="text-muted text-xs">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** The base-currency amount of a per-currency list, for charts: other currencies stay in the tables. */
function baseAmount(amounts: MoneyByCurrency, base: string): number {
  return amounts.find((entry) => entry.currencyCode === base)?.amountMinor ?? 0;
}

/**
 * The Finance tab.
 *
 * Money in, owed to the shop, owed by the shop, paid out and spent — each its own section, never
 * combined: Collections less Expenses is not profit, income or cash flow and is not shown. Receivables
 * and payables are as of now, whatever period is chosen; the period narrows flows only. Every chart
 * has a table beside it, and every figure opens into records that add up to it.
 */
export function FinanceContent({
  report,
  query,
}: {
  report: FinanceReport;
  query: FinanceReportQuery;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [open, setOpen] = useState<FinanceRecordsRequest | null>(null);
  const base = report.meta.baseCurrencyCode;
  const count = (value: number) => (
    <span dir="ltr" className="tabular-nums">
      {new Intl.NumberFormat(LOCALE_TAG[locale]).format(value)}
    </span>
  );
  const nameOf = (row: FinanceBreakdownRow, fallback: string) =>
    (locale === 'ar' && row.labelAr) || row.label || fallback;

  const view = (request: FinanceRecordsRequest) => (
    <button
      type="button"
      onClick={() => setOpen(request)}
      className="text-brand-700 mt-2 text-sm font-medium hover:underline"
      aria-label={t('reports.sales.records.viewFor', { figure: request.title })}
    >
      {t('reports.sales.records.view')}
    </button>
  );

  const records = open ? (
    <FinanceRecords
      key={`${open.kind}-${open.movement ?? ''}-${open.bucket ?? ''}-${open.settlement ?? ''}`}
      query={query}
      request={open}
      customerIdentity={report.customerIdentity}
      onClose={() => setOpen(null)}
    />
  ) : null;

  const breakdown = (title: string, rows: FinanceBreakdownRow[], unattributed: string) => (
    <Card className="min-w-0 p-4">
      <h3 className="text-ink font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-muted mt-3 text-sm">{t('reports.finance.empty')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[20rem] text-sm [&_td]:px-2 [&_th]:px-2">
            <thead>
              <tr className="text-muted border-line border-b">
                <th className="py-2 text-start font-medium">{t('reports.sales.columns.name')}</th>
                <th className="py-2 text-end font-medium">{t('reports.finance.columns.amount')}</th>
                <th className="py-2 text-end font-medium">
                  {t('reports.finance.columns.records')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id ?? 'unattributed'} className="border-line border-b last:border-b-0">
                  <td className="py-2 break-words">{nameOf(row, unattributed)}</td>
                  <td className="py-2 text-end">
                    <MoneyByCurrencyText amounts={row.amount} />
                  </td>
                  <td className="py-2 text-end">{count(row.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  const trendTable = (trend: FinanceTrend) => (
    <details className="mt-2">
      <summary className="text-brand-700 cursor-pointer text-sm font-medium">
        {t('reports.finance.showTable')}
      </summary>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[20rem] text-sm [&_td]:px-2 [&_th]:px-2">
          <thead>
            <tr className="text-muted border-line border-b">
              <th className="py-2 text-start font-medium">{t('reports.finance.columns.period')}</th>
              {trend.series.map((series) => (
                <th key={series.currencyCode} className="py-2 text-end font-medium" dir="ltr">
                  {series.currencyCode}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trend.buckets.map((bucket, index) => (
              <tr key={bucket.from} className="border-line border-b last:border-b-0">
                <td className="py-2" dir="ltr">
                  {bucket.from === bucket.to ? bucket.from : `${bucket.from} – ${bucket.to}`}
                </td>
                {trend.series.map((series) => (
                  <td key={series.currencyCode} className="py-2 text-end tabular-nums" dir="ltr">
                    {formatMoney(series.values[index] ?? 0, locale, series.currencyCode)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );

  const trendChart = (trend: FinanceTrend, label: string) => (
    <Suspense fallback={null}>
      <NetSalesTrendChart
        label={label}
        baseCurrencyCode={base}
        trend={{
          granularity: trend.granularity,
          buckets: trend.buckets,
          previousBuckets: null,
          series: trend.series.map((series) => ({
            currencyCode: series.currencyCode,
            current: series.values,
            previous: null,
          })),
        }}
      />
    </Suspense>
  );

  const ageing = (
    title: string,
    rows: FinanceAgeingRow[],
    kind: 'SCHEDULED_INSTALMENTS' | 'PAYABLE_INVOICES',
    note: string,
  ) => (
    <Card className="min-w-0 p-4">
      <h3 className="text-ink font-semibold">{title}</h3>
      <p className="text-muted text-xs">{note}</p>
      <Suspense fallback={null}>
        <FinanceMoneyChart
          label={title}
          currencyCode={base}
          data={rows.map((row) => ({
            label: t(`reports.finance.buckets.${row.bucket}`),
            amountMinor: baseAmount(row.amount, base),
          }))}
        />
      </Suspense>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[20rem] text-sm [&_td]:px-2 [&_th]:px-2">
          <thead>
            <tr className="text-muted border-line border-b">
              <th className="py-2 text-start font-medium">{t('reports.finance.columns.bucket')}</th>
              <th className="py-2 text-end font-medium">{t('reports.finance.columns.amount')}</th>
              <th className="py-2 text-end font-medium">{t('reports.finance.columns.records')}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const label = t(`reports.finance.buckets.${row.bucket}`);
              return (
                <tr key={row.bucket} className="border-line border-b last:border-b-0">
                  <td className="py-2">{label}</td>
                  <td className="py-2 text-end">
                    <MoneyByCurrencyText amounts={row.amount} />
                  </td>
                  <td className="py-2 text-end">{count(row.count)}</td>
                  <td className="py-2 text-end">
                    {row.count > 0
                      ? view({ kind, bucket: row.bucket, title: `${title} — ${label}` })
                      : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );

  const { collections, receivables, payables, supplierInvoices, supplierPayments, expenses } =
    report;
  const anything =
    collections || receivables || payables || supplierInvoices || supplierPayments || expenses;

  return (
    <div className="space-y-6">
      {report.notApplicable.length > 0 ? (
        <p className="text-muted text-sm">
          {t('reports.finance.notApplicable', {
            sections: report.notApplicable
              .map((section) => t(`reports.finance.sections.${section}`))
              .join(locale === 'ar' ? '، ' : ', '),
          })}
        </p>
      ) : null}
      {!anything ? <p className="text-muted text-sm">{t('reports.finance.noSections')}</p> : null}

      {collections || supplierPayments || expenses ? (
        <Section
          id="finance-flows"
          title={t('reports.finance.flowsTitle')}
          note={t('reports.finance.periodNote')}
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {collections ? (
              <Stat
                label={t('reports.finance.kpis.collections')}
                hint={t('reports.finance.hints.collections', { count: collections.payments.count })}
                action={view({
                  kind: 'COLLECTION_TRANSACTIONS',
                  title: t('reports.finance.kpis.collections'),
                })}
              >
                <MoneyByCurrencyText amounts={collections.collections} />
              </Stat>
            ) : null}
            {collections ? (
              <Stat
                label={t('reports.finance.kpis.refunds')}
                hint={t('reports.finance.hints.refunds', { count: collections.refunds.count })}
                action={view({
                  kind: 'COLLECTION_TRANSACTIONS',
                  movement: 'REFUND',
                  title: t('reports.finance.kpis.refunds'),
                })}
              >
                <MoneyByCurrencyText amounts={collections.refunds.amount} />
              </Stat>
            ) : null}
            {supplierPayments ? (
              <Stat
                label={t('reports.finance.kpis.supplierPayments')}
                hint={t('reports.finance.hints.payments', { count: supplierPayments.paid.count })}
                action={view({
                  kind: 'SUPPLIER_PAYMENTS',
                  title: t('reports.finance.kpis.supplierPayments'),
                })}
              >
                <MoneyByCurrencyText amounts={supplierPayments.paid.amount} />
              </Stat>
            ) : null}
            {expenses ? (
              <Stat
                label={t('reports.finance.kpis.expenses')}
                hint={t('reports.finance.hints.expenses', { count: expenses.expenses.count })}
                action={view({ kind: 'EXPENSES', title: t('reports.finance.kpis.expenses') })}
              >
                <MoneyByCurrencyText amounts={expenses.expenses.amount} />
              </Stat>
            ) : null}
          </div>
        </Section>
      ) : null}

      {receivables || payables ? (
        <Section
          id="finance-now"
          title={t('reports.finance.nowTitle')}
          note={t('reports.finance.snapshotNote')}
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {receivables ? (
              <Stat
                label={t('reports.finance.kpis.receivables')}
                hint={t('reports.finance.hints.owingOrders', { count: receivables.owingOrders })}
                action={view({
                  kind: 'RECEIVABLE_ORDERS',
                  title: t('reports.finance.kpis.receivables'),
                })}
              >
                <MoneyByCurrencyText amounts={receivables.outstanding} />
              </Stat>
            ) : null}
            {receivables ? (
              <Stat
                label={t('reports.finance.kpis.scheduled')}
                hint={t('reports.finance.hints.instalments', {
                  count: receivables.scheduled.count,
                })}
                action={view({
                  kind: 'SCHEDULED_INSTALMENTS',
                  title: t('reports.finance.kpis.scheduled'),
                })}
              >
                <MoneyByCurrencyText amounts={receivables.scheduled.amount} />
              </Stat>
            ) : null}
            {receivables ? (
              <Stat
                label={t('reports.finance.kpis.overdueInstalments')}
                hint={t('reports.finance.hints.instalments', { count: receivables.overdue.count })}
              >
                <MoneyByCurrencyText amounts={receivables.overdue.amount} />
              </Stat>
            ) : null}
            {payables ? (
              <Stat
                label={t('reports.finance.kpis.payables')}
                hint={t('reports.finance.hints.openInvoices', { count: payables.openInvoices })}
                action={view({
                  kind: 'PAYABLE_INVOICES',
                  title: t('reports.finance.kpis.payables'),
                })}
              >
                <MoneyByCurrencyText amounts={payables.outstanding} />
              </Stat>
            ) : null}
            {payables ? (
              <Stat
                label={t('reports.finance.kpis.overdueInvoices')}
                hint={t('reports.finance.hints.openInvoices', { count: payables.overdue.count })}
              >
                <MoneyByCurrencyText amounts={payables.overdue.amount} />
              </Stat>
            ) : null}
          </div>
          {receivables ? (
            <p className="text-muted text-xs">{t('reports.finance.receivablesNote')}</p>
          ) : null}
        </Section>
      ) : null}

      {records}

      {collections ? (
        <Section
          id="finance-collections"
          title={t('reports.finance.sections.COLLECTIONS')}
          note={t('reports.finance.collectionsNote')}
        >
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.finance.collectionsTrend')}</h3>
            {trendChart(collections.trend, t('reports.finance.kpis.collections'))}
            {trendTable(collections.trend)}
          </Card>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.finance.methodsTitle')}</h3>
            <p className="text-muted text-xs">{t('reports.finance.methodsNote')}</p>
            <Suspense fallback={null}>
              <FinanceMoneyChart
                label={t('reports.finance.methodsTitle')}
                currencyCode={base}
                data={collections.byMethod
                  .filter((row) => row.currencyCode === base)
                  .map((row) => ({
                    label: t(`sales.methods.${row.method}`),
                    amountMinor: row.netMinor,
                  }))}
              />
            </Suspense>
            {collections.byMethod.length === 0 ? (
              <p className="text-muted mt-3 text-sm">{t('reports.finance.empty')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[32rem] text-sm [&_td]:px-2 [&_th]:px-2">
                  <thead>
                    <tr className="text-muted border-line border-b">
                      <th className="py-2 text-start font-medium">
                        {t('reports.finance.columns.method')}
                      </th>
                      <th className="py-2 text-end font-medium">
                        {t('reports.finance.columns.payments')}
                      </th>
                      <th className="py-2 text-end font-medium">
                        {t('reports.finance.columns.refunds')}
                      </th>
                      <th className="py-2 text-end font-medium">
                        {t('reports.finance.columns.net')}
                      </th>
                      <th className="py-2 text-end font-medium">
                        {t('reports.finance.columns.transactions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {collections.byMethod.map((row) => (
                      <tr
                        key={`${row.method}-${row.currencyCode}`}
                        className="border-line border-b last:border-b-0"
                      >
                        <td className="py-2">{t(`sales.methods.${row.method}`)}</td>
                        <td className="py-2 text-end tabular-nums" dir="ltr">
                          {formatMoney(row.paymentsMinor, locale, row.currencyCode)}
                        </td>
                        <td className="py-2 text-end tabular-nums" dir="ltr">
                          {formatMoney(-row.refundsMinor, locale, row.currencyCode)}
                        </td>
                        <td className="text-ink py-2 text-end font-medium tabular-nums" dir="ltr">
                          {formatMoney(row.netMinor, locale, row.currencyCode)}
                        </td>
                        <td className="py-2 text-end">
                          {count(row.paymentCount + row.refundCount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          {breakdown(
            t('reports.finance.byBranch'),
            collections.byBranch,
            t('reports.sales.unattributed'),
          )}
        </Section>
      ) : null}

      {receivables ? (
        <Section
          id="finance-receivables"
          title={t('reports.finance.sections.RECEIVABLES')}
          note={t('reports.finance.snapshotNote')}
        >
          {ageing(
            t('reports.finance.planAgeing'),
            receivables.ageing,
            'SCHEDULED_INSTALMENTS',
            t('reports.finance.planAgeingNote'),
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            {breakdown(
              t('reports.finance.outstandingByBranch'),
              receivables.byBranch,
              t('reports.sales.unattributed'),
            )}
            {breakdown(
              t('reports.finance.scheduledByBranch'),
              receivables.scheduledByBranch,
              t('reports.sales.unattributed'),
            )}
          </div>
        </Section>
      ) : null}

      {payables ? (
        <Section
          id="finance-payables"
          title={t('reports.finance.sections.PAYABLES')}
          note={t('reports.finance.payablesNote')}
        >
          {ageing(
            t('reports.finance.payableAgeing'),
            payables.ageing,
            'PAYABLE_INVOICES',
            t('reports.finance.payableAgeingNote'),
          )}
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.finance.settlementTitle')}</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[20rem] text-sm [&_td]:px-2 [&_th]:px-2">
                <thead>
                  <tr className="text-muted border-line border-b">
                    <th className="py-2 text-start font-medium">
                      {t('reports.finance.columns.settlement')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.finance.columns.outstanding')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.finance.columns.invoices')}
                    </th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {payables.bySettlement.map((row) => {
                    const label = t(`payables.settlement.${row.settlement}`);
                    return (
                      <tr key={row.settlement} className="border-line border-b last:border-b-0">
                        <td className="py-2">{label}</td>
                        <td className="py-2 text-end">
                          <MoneyByCurrencyText amounts={row.outstanding} />
                        </td>
                        <td className="py-2 text-end">{count(row.count)}</td>
                        <td className="py-2 text-end">
                          {row.count > 0
                            ? view({
                                kind: 'PAYABLE_INVOICES',
                                settlement: row.settlement,
                                title: `${t('reports.finance.settlementTitle')} — ${label}`,
                              })
                            : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            {breakdown(
              t('reports.finance.bySupplier'),
              payables.bySupplier,
              t('reports.sales.unattributed'),
            )}
            {breakdown(
              t('reports.finance.byBranch'),
              payables.byBranch,
              t('reports.sales.unattributed'),
            )}
          </div>
        </Section>
      ) : null}

      {supplierInvoices ? (
        <Section
          id="finance-invoices"
          title={t('reports.finance.sections.SUPPLIER_INVOICES')}
          note={t('reports.finance.invoicesNote')}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Stat
              label={t('reports.finance.kpis.invoiced')}
              hint={t('reports.finance.hints.invoices', { count: supplierInvoices.invoiced.count })}
              action={view({
                kind: 'SUPPLIER_INVOICES',
                title: t('reports.finance.kpis.invoiced'),
              })}
            >
              <MoneyByCurrencyText amounts={supplierInvoices.invoiced.amount} />
            </Stat>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {breakdown(
              t('reports.finance.bySupplier'),
              supplierInvoices.bySupplier,
              t('reports.sales.unattributed'),
            )}
            {breakdown(
              t('reports.finance.byBranch'),
              supplierInvoices.byBranch,
              t('reports.sales.unattributed'),
            )}
          </div>
        </Section>
      ) : null}

      {supplierPayments ? (
        <Section
          id="finance-supplier-payments"
          title={t('reports.finance.sections.SUPPLIER_PAYMENTS')}
          note={t('reports.finance.supplierPaymentsNote')}
        >
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.finance.supplierMethodsTitle')}</h3>
            {supplierPayments.byMethod.length === 0 ? (
              <p className="text-muted mt-3 text-sm">{t('reports.finance.empty')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[20rem] text-sm [&_td]:px-2 [&_th]:px-2">
                  <thead>
                    <tr className="text-muted border-line border-b">
                      <th className="py-2 text-start font-medium">
                        {t('reports.finance.columns.method')}
                      </th>
                      <th className="py-2 text-end font-medium">
                        {t('reports.finance.columns.amount')}
                      </th>
                      <th className="py-2 text-end font-medium">
                        {t('reports.finance.columns.payments')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierPayments.byMethod.map((row) => (
                      <tr
                        key={`${row.method}-${row.currencyCode}`}
                        className="border-line border-b last:border-b-0"
                      >
                        <td className="py-2">{t(`sales.methods.${row.method}`)}</td>
                        <td className="py-2 text-end tabular-nums" dir="ltr">
                          {formatMoney(row.amountMinor, locale, row.currencyCode)}
                        </td>
                        <td className="py-2 text-end">{count(row.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            {breakdown(
              t('reports.finance.bySupplier'),
              supplierPayments.bySupplier,
              t('reports.sales.unattributed'),
            )}
            {breakdown(
              t('reports.finance.byBranch'),
              supplierPayments.byBranch,
              t('reports.sales.unattributed'),
            )}
          </div>
        </Section>
      ) : null}

      {expenses ? (
        <Section
          id="finance-expenses"
          title={t('reports.finance.sections.EXPENSES')}
          note={t('reports.finance.expensesNote')}
        >
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.finance.byCategory')}</h3>
            <Suspense fallback={null}>
              <FinanceMoneyChart
                label={t('reports.finance.byCategory')}
                currencyCode={base}
                data={expenses.byCategory.map((row) => ({
                  label: nameOf(row, t('reports.sales.unattributed')),
                  amountMinor: baseAmount(row.amount, base),
                }))}
              />
            </Suspense>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            {breakdown(
              t('reports.finance.byCategory'),
              expenses.byCategory,
              t('reports.sales.unattributed'),
            )}
            {breakdown(
              t('reports.finance.byBranch'),
              expenses.byBranch,
              t('reports.sales.unattributed'),
            )}
          </div>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.finance.expensesTrend')}</h3>
            {trendChart(expenses.trend, t('reports.finance.kpis.expenses'))}
            {trendTable(expenses.trend)}
          </Card>
        </Section>
      ) : null}
    </div>
  );
}
